// src/lib/licenseValidation.ts
/**
 * License validation and expiration checking utilities
 * Ensures Pro users maintain active, unexpired licenses for verified status
 */

export interface LicenseDocument {
  jurisdictionKey: string;
  licenseNumber: string;
  issuedAt?: string;
  expiresAt?: string; // ISO date string
  filePath: string;
  fileUrl?: string;
}

export interface LicenseValidationResult {
  hasActiveLicense: boolean;
  hasExpiringLicense: boolean; // Within 30 days
  expiredLicenses: LicenseDocument[];
  expiringLicenses: LicenseDocument[];
  activeLicenses: LicenseDocument[];
  earliestExpirationDate: Date | null;
  daysUntilExpiration: number | null;
}

const EXPIRATION_WARNING_DAYS = 30; // Warn 30 days before expiration

/**
 * Parse ISO date string to Date object
 */
function parseISODate(isoString: string | undefined): Date | null {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Check if a license is expired
 */
function isExpired(expiresAt: string | undefined): boolean {
  const expirationDate = parseISODate(expiresAt);
  if (!expirationDate) return false; // No expiration date = never expires
  
  const now = new Date();
  return now > expirationDate;
}

/**
 * Check if a license is expiring soon (within warning period)
 */
function isExpiringSoon(expiresAt: string | undefined): boolean {
  const expirationDate = parseISODate(expiresAt);
  if (!expirationDate) return false;
  
  const now = new Date();
  const warningDate = new Date(now.getTime() + (EXPIRATION_WARNING_DAYS * 24 * 60 * 60 * 1000));
  
  return now < expirationDate && expirationDate <= warningDate;
}

/**
 * Calculate days until expiration
 * Exported for testing and external use
 */
export function getDaysUntilExpiration(expiresAt: string | undefined): number | null {
  const expirationDate = parseISODate(expiresAt);
  if (!expirationDate) return null;
  
  const now = new Date();
  const diffMs = expirationDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
  
  return diffDays;
}

/**
 * Validate all licenses for a professional
 * Returns detailed status about active, expiring, and expired licenses
 */
export function validateLicenses(licenses: LicenseDocument[] | undefined): LicenseValidationResult {
  if (!licenses || licenses.length === 0) {
    return {
      hasActiveLicense: false,
      hasExpiringLicense: false,
      expiredLicenses: [],
      expiringLicenses: [],
      activeLicenses: [],
      earliestExpirationDate: null,
      daysUntilExpiration: null,
    };
  }

  const expired: LicenseDocument[] = [];
  const expiring: LicenseDocument[] = [];
  const active: LicenseDocument[] = [];
  let earliestExpiration: Date | null = null;

  for (const license of licenses) {
    if (isExpired(license.expiresAt)) {
      expired.push(license);
    } else if (isExpiringSoon(license.expiresAt)) {
      expiring.push(license);
      active.push(license); // Still active, just expiring soon
      
      const expDate = parseISODate(license.expiresAt);
      if (expDate && (!earliestExpiration || expDate < earliestExpiration)) {
        earliestExpiration = expDate;
      }
    } else {
      active.push(license);
      
      // Track earliest expiration even for licenses not yet in warning period
      const expDate = parseISODate(license.expiresAt);
      if (expDate && (!earliestExpiration || expDate < earliestExpiration)) {
        earliestExpiration = expDate;
      }
    }
  }

  const daysUntil = earliestExpiration 
    ? Math.ceil((earliestExpiration.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : null;

  return {
    hasActiveLicense: active.length > 0,
    hasExpiringLicense: expiring.length > 0,
    expiredLicenses: expired,
    expiringLicenses: expiring,
    activeLicenses: active,
    earliestExpirationDate: earliestExpiration,
    daysUntilExpiration: daysUntil,
  };
}

/**
 * Determine if a professional should be marked as unverified due to license issues
 * A Pro should be unverified if:
 * 1. They have no active licenses
 * 2. All their licenses are expired
 */
export function shouldBeUnverified(licenses: LicenseDocument[] | undefined): boolean {
  const validation = validateLicenses(licenses);
  return !validation.hasActiveLicense || validation.expiredLicenses.length === licenses?.length;
}

/**
 * Get user-friendly license status message
 */
export function getLicenseStatusMessage(licenses: LicenseDocument[] | undefined): string {
  const validation = validateLicenses(licenses);
  
  if (!validation.hasActiveLicense) {
    return 'No active license on file';
  }
  
  if (validation.expiredLicenses.length > 0) {
    const count = validation.expiredLicenses.length;
    return `${count} expired license${count > 1 ? 's' : ''} - verification suspended`;
  }
  
  if (validation.hasExpiringLicense && validation.daysUntilExpiration !== null) {
    const days = validation.daysUntilExpiration;
    return `License expires in ${days} day${days !== 1 ? 's' : ''} - please renew`;
  }
  
  return 'All licenses active';
}
